import React, { useEffect, useState } from "react";
import { Menu, MenuItem, Popover } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import useTheme from "../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../providers/ThemeProvider";
import PeriodNavigator from "../../common/PeriodNavigator";
import ChartKeyPicker from "../../dashboard/ChartKeyPicker";
import FilterPicker from "../../dashboard/ChartTargetPicker";
import ServiceLanePicker from "./ServiceLanePicker";
import { ServiceLaneKey } from "./serviceLanes";
import { TimelineCatalog } from "./useTimelineSeries";
import { TimelinePeriod, TimelineWindow } from "./timeScale";

/**
 * combined: metrics, tests and services; chart: the metrics alone;
 * events: tests per kind and every service on its own row, no metrics.
 */
export type TimelineView = "combined" | "chart" | "events";
/** The views that chart metrics, and so carry the Target and Filters pickers. */
export const hasMetrics = (view: TimelineView) => view !== "events";
/** The views that draw the service lanes, and so carry the Services picker. */
export const hasLanes = (view: TimelineView) => view !== "chart";

export const MAX_TIMELINE_METRICS = 5;

const PERIODS: { value: TimelinePeriod; label: string }[] = [
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];

const VIEWS: { value: TimelineView; label: string }[] = [
  { value: "combined", label: "Full timeline" },
  { value: "chart", label: "Metrics only" },
  { value: "events", label: "Events & Services" },
];

type Props = {
  window: TimelineWindow;
  period: TimelinePeriod;
  onPeriodChange: (period: TimelinePeriod) => void;
  onShift: (direction: -1 | 1) => void;
  onToday: () => void;
  view: TimelineView;
  onViewChange: (view: TimelineView) => void;
  catalog: TimelineCatalog;
  /** The PNOĒ metrics come from the report, which may still be loading. */
  metricsLoading: boolean;
  selectedKeys: string[];
  onSelectionChange: (keys: string[]) => void;
  /** Selected series that have a goal to draw. */
  targetOptions: { key: string; label: string }[];
  targetKeys: string[];
  onTargetChange: (keys: string[]) => void;
  /** Whether the timeline fills the screen (TimelineView's dialog). */
  fullscreen: boolean;
  onToggleFullscreen: () => void;
  /** The service lanes' order and their service counts in the period. */
  laneOrder: ServiceLaneKey[];
  laneCounts: Partial<Record<ServiceLaneKey, number>>;
  onLaneOrderChange: (order: ServiceLaneKey[]) => void;
};

/** A dropdown trigger in the design's outlined style. */
const DropdownButton = ({
  open,
  onClick,
  children,
}: {
  open: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}) => {
  const theme: Theme = useTheme();
  return (
    <button type="button" style={styles.dropdown(theme, open)} onClick={onClick}>
      <span style={styles.dropdownLabel(theme)}>{children}</span>
      <KeyboardArrowDownIcon
        style={{
          fontSize: "18px",
          color: theme.colors_ds.NEUTRAL_ICON_DEFAULT,
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.3s ease",
        }}
      />
    </button>
  );
};

/** The expand / collapse toggle, in the same outlined style as the dropdowns. */
const IconToggleButton = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => {
  const theme: Theme = useTheme();
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      style={styles.iconButton(theme)}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const popoverPaper = {
  sx: { marginTop: "8px", boxShadow: "var(--box-shadow-general)" },
};

const TimelineToolbar = ({
  window,
  period,
  onPeriodChange,
  onShift,
  onToday,
  view,
  onViewChange,
  catalog,
  metricsLoading,
  selectedKeys,
  onSelectionChange,
  targetOptions,
  targetKeys,
  onTargetChange,
  fullscreen,
  onToggleFullscreen,
  laneOrder,
  laneCounts,
  onLaneOrderChange,
}: Props) => {
  const theme: Theme = useTheme();
  const [viewAnchor, setViewAnchor] = useState<HTMLElement | null>(null);
  const [targetAnchor, setTargetAnchor] = useState<HTMLElement | null>(null);
  const [filtersAnchor, setFiltersAnchor] = useState<HTMLElement | null>(null);
  const [servicesAnchor, setServicesAnchor] = useState<HTMLElement | null>(null);

  // Target and Filters exist only in the views with metrics; switching away
  // unmounts their buttons, so a stored anchor would point at a detached node.
  useEffect(() => {
    setTargetAnchor(null);
    setFiltersAnchor(null);
    setServicesAnchor(null);
  }, [view]);

  // The Target button goes away when the last wearable is deselected; close
  // its popover with it rather than leave it anchored to a detached node.
  const hasTargets = targetOptions.length > 0;
  useEffect(() => {
    if (!hasTargets) setTargetAnchor(null);
  }, [hasTargets]);

  // Nothing to reorder with a single lane in the period: no Services button.
  const canReorderLanes =
    hasLanes(view) &&
    Object.values(laneCounts).filter((count) => (count ?? 0) > 0).length > 1;
  useEffect(() => {
    if (!canReorderLanes) setServicesAnchor(null);
  }, [canReorderLanes]);

  return (
    <div style={styles.container}>
      <div style={styles.group}>
        <div style={styles.periodNav}>
          <PeriodNavigator
            label={window.label}
            period={period}
            containsToday={window.containsToday}
            onPrevious={() => onShift(-1)}
            onNext={() => onShift(1)}
            onToday={onToday}
          />
        </div>

        <div style={styles.segmented(theme)}>
          {PERIODS.map((option) => (
            <div
              key={option.value}
              style={styles.segment(theme, option.value === period)}
              onClick={() => onPeriodChange(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>

        <DropdownButton
          open={Boolean(viewAnchor)}
          onClick={(event) => setViewAnchor(event.currentTarget)}
        >
          {VIEWS.find((v) => v.value === view)?.label}
        </DropdownButton>
        <Menu
          anchorEl={viewAnchor}
          open={Boolean(viewAnchor)}
          onClose={() => setViewAnchor(null)}
          PaperProps={popoverPaper}
        >
          {VIEWS.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === view}
              sx={styles.menuItem(theme)}
              onClick={() => {
                onViewChange(option.value);
                setViewAnchor(null);
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>

        {/* Lane order, beside the view it applies to (not in Metrics only). */}
        {canReorderLanes && (
          <>
            <DropdownButton
              open={Boolean(servicesAnchor)}
              onClick={(event) => setServicesAnchor(event.currentTarget)}
            >
              Services order
            </DropdownButton>
            <Popover
              open={Boolean(servicesAnchor) && canReorderLanes}
              anchorEl={servicesAnchor}
              onClose={() => setServicesAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={popoverPaper}
            >
              <ServiceLanePicker
                order={laneOrder}
                counts={laneCounts}
                onChange={onLaneOrderChange}
              />
            </Popover>
          </>
        )}
      </div>

      <div style={styles.group}>
        {hasMetrics(view) && (
          <>
          {targetOptions.length > 0 && (
            <>
              <DropdownButton
                open={Boolean(targetAnchor)}
                onClick={(event) => setTargetAnchor(event.currentTarget)}
              >
                Target
              </DropdownButton>
              <Popover
                open={Boolean(targetAnchor) && hasMetrics(view)}
                anchorEl={targetAnchor}
                onClose={() => setTargetAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={popoverPaper}
              >
                <FilterPicker
                  title="Show goal"
                  data={targetOptions.map((o) => o.key)}
                  options={targetOptions}
                  selectedKeys={targetKeys}
                  onSelectionChange={onTargetChange}
                  showOriginLogic
                />
              </Popover>
            </>
          )}

          <DropdownButton
            open={Boolean(filtersAnchor)}
            onClick={(event) => setFiltersAnchor(event.currentTarget)}
          >
            Filters
            <span style={styles.count(theme)}>{selectedKeys.length}</span>
          </DropdownButton>
          <Popover
            open={Boolean(filtersAnchor) && hasMetrics(view)}
            anchorEl={filtersAnchor}
            onClose={() => setFiltersAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={popoverPaper}
          >
            <ChartKeyPicker
              selectedKeys={selectedKeys}
              onSelectionChange={onSelectionChange}
              max={MAX_TIMELINE_METRICS}
              sections={[
                {
                  title: "PNOE metrics",
                  data: catalog.metrics,
                  emptyMessage: metricsLoading ? "Loading PNOĒ metrics…" : undefined,
                },
                { title: "Biomarkers", data: catalog.biomarkers },
                { title: "Wearable data", data: catalog.wearables },
              ]}
            />
          </Popover>
          </>
        )}

        <IconToggleButton
          label={fullscreen ? "Exit full screen" : "Full screen"}
          onClick={onToggleFullscreen}
        >
          {fullscreen ? (
            <FullscreenExitIcon style={styles.icon(theme)} />
          ) : (
            <FullscreenIcon style={styles.icon(theme)} />
          )}
        </IconToggleButton>
      </div>
    </div>
  );
};

export default TimelineToolbar;

const styles = {
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap" as "wrap",
    marginBottom: "16px",
  },
  group: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  periodNav: {
    marginRight: "8px",
  },
  segmented: (theme: Theme) => ({
    display: "flex",
    padding: "3px",
    gap: "2px",
    borderRadius: "8px",
    backgroundColor: theme.colorTokens.N[40],
  }),
  segment: (theme: Theme, active: boolean) => ({
    minWidth: "62px",
    padding: "6px 14px",
    borderRadius: "6px",
    textAlign: "center" as "center",
    cursor: "pointer",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    backgroundColor: active ? theme.colors_ds.NEUTRAL_BG_MAIN : "transparent",
    color: active ? theme.colors_ds.NEUTRAL_TEXT_STRONG : theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  dropdown: (theme: Theme, open: boolean) => ({
    height: "34px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "0 8px 0 12px",
    borderRadius: "8px",
    border: `1px solid ${
      open ? theme.colors_ds.PRIMARY_ICON_MAIN : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    backgroundColor: open ? theme.colorTokens.P[10] : theme.colors_ds.NEUTRAL_BG_MAIN,
    cursor: "pointer",
    outline: "none",
  }),
  iconButton: (theme: Theme) => ({
    width: "34px",
    height: "34px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    cursor: "pointer",
    outline: "none",
  }),
  icon: (theme: Theme) => ({
    fontSize: "20px",
    color: theme.colors_ds.NEUTRAL_ICON_DEFAULT,
  }),
  dropdownLabel: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    whiteSpace: "nowrap" as "nowrap",
  }),
  count: (theme: Theme) => ({
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
    color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    fontFamily: "var(--font-Bold)",
    fontSize: "11px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  menuItem: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
};
